/**
 * CZNA-1470 — the vocabulary types a picker offers, read from the registry.
 *
 * <p>`VOCABULARY_TYPE_CONFIG` is the 17 types the FE knew when it was written; the
 * registry holds every type, including one a vocabulary administrator created a
 * minute ago in Nastavení → Slovníky. A list built from the config alone never shows
 * that new type, so the template editor could not bind a subfield to it and the term
 * list could not filter by it. The config stays as the label fallback while the
 * registry has not answered (and for tests that do not stub it).
 *
 * <p>The label is the registry's — `labelEn` in an English locale when it has one,
 * `labelCs` otherwise — so a rename in Nastavení → Slovníky shows everywhere.
 */
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useVocabularyTypes } from './useVocabularyTypes';
import { VOCABULARY_TYPE_CONFIG } from '../types/api';
/** The display label of one registry row in the given locale. */
export var vocabularyTypeLabel = function (type, locale) { return (locale && !locale.startsWith('cs') && type.labelEn ? type.labelEn : type.labelCs); };
export var useVocabularyTypeOptions = function (opts) {
    if (opts === void 0) { opts = {}; }
    var intl = useIntl();
    var validationType = opts.validationType, _a = opts.enabled, enabled = _a === void 0 ? true : _a;
    var _b = useVocabularyTypes({ enabled: enabled }), vocabularyTypes = _b.vocabularyTypes, isLoading = _b.isLoading, isError = _b.isError;
    var registry = useMemo(function () {
        var map = new Map();
        for (var _i = 0, vocabularyTypes_1 = vocabularyTypes; _i < vocabularyTypes_1.length; _i++) {
            var t = vocabularyTypes_1[_i];
            map.set(t.code, t);
        }
        return map;
    }, [vocabularyTypes]);
    var labelOf = useCallback(function (code) {
        var t = registry.get(code);
        if (t)
            return vocabularyTypeLabel(t, intl.locale);
        var config = VOCABULARY_TYPE_CONFIG[code];
        return config ? intl.formatMessage({ id: config.labelKey }) : code;
    }, [registry, intl]);
    var options = useMemo(function () {
        var codes = new Set();
        for (var _i = 0, _a = Object.keys(VOCABULARY_TYPE_CONFIG); _i < _a.length; _i++) {
            var code = _a[_i];
            var t = registry.get(code);
            if (!validationType || (t ? t.validationType : 'OPEN') === validationType)
                codes.add(code);
        }
        for (var _b = 0, vocabularyTypes_2 = vocabularyTypes; _b < vocabularyTypes_2.length; _b++) {
            var t = vocabularyTypes_2[_b];
            if (!validationType || t.validationType === validationType)
                codes.add(t.code);
        }
        return Array.from(codes)
            .map(function (code) { return ({ value: code, label: labelOf(code) }); })
            .sort(function (a, b) { return a.label.localeCompare(b.label, 'cs'); });
    }, [registry, vocabularyTypes, validationType, labelOf]);
    /** The registry row of a code, when the registry has one. */
    var typeOf = useCallback(function (code) { return registry.get(code); }, [registry]);
    return {
        options: options,
        labelOf: labelOf,
        typeOf: typeOf,
        isLoading: isLoading,
        isError: isError,
    };
};
export default useVocabularyTypeOptions;
